import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { errText } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Briefcase, Buildings, Scales, ChartLineUp, Globe, Receipt, Robot, Stamp, Sparkle, CheckCircle, CircleNotch, ArrowLeft } from "@phosphor-icons/react";

const ICON_MAP = {
  briefcase: Briefcase, buildings: Buildings, scales: Scales,
  "chart-line-up": ChartLineUp, globe: Globe, receipt: Receipt,
  robot: Robot, stamp: Stamp, sparkle: Sparkle,
};

const CAT_STYLES = {
  advisory: "bg-indigo-50 text-indigo-800 border-indigo-200",
  compliance: "bg-slate-50 text-slate-800 border-slate-200",
  accounting: "bg-emerald-50 text-emerald-800 border-emerald-200",
  ai: "bg-cyan-50 text-cyan-800 border-cyan-200",
  tax: "bg-amber-50 text-amber-800 border-amber-200",
  custom: "bg-rose-50 text-rose-800 border-rose-200",
};

export default function ServiceCatalogDialog({ open, onOpenChange, entityId, trigger }) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");

  const { data: catalog, isLoading } = useQuery({
    queryKey: ["service-catalog"],
    queryFn: () => api.get("/service-catalog").then((r) => r.data),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  const { data: myRequests } = useQuery({
    queryKey: ["my-service-requests"],
    queryFn: () => api.get("/portal/service-requests").then((r) => r.data),
    enabled: open,
  });

  const pendingByKey = (myRequests || []).reduce((m, r) => {
    if (r.status === "pending") m[r.service_key] = r;
    return m;
  }, {});

  const submitRequest = useMutation({
    mutationFn: () => api.post("/portal/service-requests", {
      service_key: selected.key, entity_id: entityId || null, notes,
    }),
    onSuccess: () => {
      toast.success(`Request sent — your CPA will review "${selected.title}" shortly.`);
      qc.invalidateQueries({ queryKey: ["my-service-requests"] });
      setSelected(null); setNotes("");
    },
    onError: (e) => toast.error(errText(e)),
  });

  const services = catalog?.services || [];

  return (
    <>
      {trigger && <div onClick={() => onOpenChange(true)} data-testid="service-catalog-open-trigger">{trigger}</div>}
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="service-catalog-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "'Cardo', Georgia, serif" }}>
              <Sparkle size={18} weight="fill" className="text-emerald-600" />
              {selected ? selected.title : "Service Catalog"}
            </DialogTitle>
            <DialogDescription>
              {selected
                ? "Review the scope, then send this to your CPA to open a new engagement."
                : "Choose a service — your CPA will review the request and open a scoped workspace."}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="py-10 text-center"><CircleNotch size={20} className="animate-spin mx-auto text-zinc-400" /></div>
            ) : selected ? (
              <ServiceDetailPane
                service={selected}
                pending={pendingByKey[selected.key]}
                notes={notes} setNotes={setNotes}
                onBack={() => setSelected(null)}
                onSubmit={() => submitRequest.mutate()}
                submitting={submitRequest.isPending}
              />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 py-2" data-testid="service-catalog-grid">
                {services.map((s) => {
                  const Icon = ICON_MAP[s.icon] || Sparkle;
                  const pending = pendingByKey[s.key];
                  return (
                    <button
                      key={s.key}
                      data-testid={`service-catalog-card-${s.key}`}
                      onClick={() => setSelected(s)}
                      className="text-left rounded-lg border border-zinc-200 hover:border-slate-900 hover:shadow-sm bg-white p-3.5 transition-all duration-200 group"
                    >
                      <div className="flex items-start gap-2.5">
                        <div className={`shrink-0 rounded-md p-1.5 ${CAT_STYLES[s.category] || CAT_STYLES.tax}`}>
                          <Icon size={16} weight="fill" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <h3 className="text-sm font-semibold text-slate-900">{s.title}</h3>
                            <span className={`text-[9px] uppercase tracking-wider rounded-sm border px-1 py-0.5 ${CAT_STYLES[s.category] || CAT_STYLES.tax}`}>{s.category}</span>
                            {pending && (
                              <span className="text-[9px] uppercase tracking-wider rounded-sm bg-amber-50 text-amber-800 border border-amber-200 px-1 py-0.5" data-testid={`service-pending-${s.key}`}>
                                Pending approval
                              </span>
                            )}
                          </div>
                          <p className="text-[11.5px] text-zinc-600 leading-snug mt-1">{s.summary}</p>
                          <p className="text-[10px] text-zinc-400 mt-1.5">{s.typical_timeline}</p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ServiceDetailPane({ service, pending, notes, setNotes, onBack, onSubmit, submitting }) {
  return (
    <div className="space-y-4" data-testid={`service-detail-${service.key}`}>
      <button onClick={onBack} className="inline-flex items-center gap-1 text-[11px] text-zinc-500 hover:text-slate-900 transition-colors duration-150" data-testid="service-detail-back">
        <ArrowLeft size={11} /> Back to catalog
      </button>

      <p className="text-sm text-zinc-700 leading-relaxed">{service.summary}</p>

      <div>
        <p className="text-[10.5px] uppercase tracking-wider text-zinc-500 mb-1.5">What's included</p>
        <ul className="space-y-1">
          {service.scope.map((s, i) => (
            <li key={`${service.key}-scope-${i}-${s.slice(0, 20)}`} className="flex items-start gap-1.5 text-[12px] text-slate-800">
              <CheckCircle size={11} weight="fill" className="text-emerald-600 mt-0.5 shrink-0" /> {s}
            </li>
          ))}
        </ul>
      </div>

      <div>
        <p className="text-[10.5px] uppercase tracking-wider text-zinc-500 mb-1.5">Documents we'll need</p>
        <ul className="space-y-0.5">
          {service.required_docs.map((d, i) => (
            <li key={`${service.key}-doc-${i}-${d.slice(0, 20)}`} className="text-[11.5px] text-zinc-600">• {d}</li>
          ))}
        </ul>
      </div>

      <p className="text-[11px] text-zinc-500">
        <span className="font-medium text-slate-800">Timeline:</span> {service.typical_timeline}
      </p>

      {pending ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3" data-testid="service-detail-already-pending">
          <p className="text-[12px] text-amber-900">
            You already have a pending request for this service (submitted {new Date(pending.created_at).toLocaleDateString()}). Your CPA will follow up shortly.
          </p>
        </div>
      ) : (
        <div>
          <label className="block text-[10.5px] uppercase tracking-wider text-zinc-500 mb-1">Notes for your CPA (optional)</label>
          <textarea
            data-testid="service-detail-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="What sparked this? Any deadlines or context we should know?"
            className="w-full text-sm border border-zinc-200 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-slate-900"
          />
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" size="sm" onClick={onBack} data-testid="service-detail-cancel">Cancel</Button>
            <Button size="sm" onClick={onSubmit} disabled={submitting} data-testid="service-detail-submit">
              {submitting ? <><CircleNotch size={13} className="animate-spin mr-1" /> Sending…</> : "Send to CPA for approval"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
